import React, { useState } from "react";
import { PortfolioItem } from '@/hooks/useProfile';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, MoreVertical, FileEdit, GripVertical, Star, Undo } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

// Helper function to strip HTML tags and get plain text
const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

interface PortfolioItemCardProps {
  item: PortfolioItem;
  index: number;
  onRemove: (index: number) => void;
  onEdit: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
  isDeleting?: boolean;
  isProcessing?: boolean;
  onSetFeatured?: (id: string) => void;
  onUpdateStatus?: (id: string, status: "draft" | "published") => void;
}

const PortfolioItemCard = ({ 
  item, 
  index, 
  onRemove, 
  onEdit, 
  onRename,
  isDeleting = false,
  isProcessing = false,
  onSetFeatured,
  onUpdateStatus,
}: PortfolioItemCardProps) => {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(item.title);
  
  const handleRename = () => {
    if (newTitle.trim() !== '') {
      onRename(item.id, newTitle);
      setIsRenameDialogOpen(false);
    }
  };
  
  // Get the first image to display (legacy support)
  const displayImage = item.images && item.images.length > 0 
    ? item.images[0].url 
    : item.image_url || '/placeholder.svg';
  
  const cardOpacity = isDeleting ? 'opacity-50' : 'opacity-100';
  const itemTitle = item.title || 'Unnamed Project';
  
  return (
    <Card className={`overflow-hidden transition-all duration-200 ${cardOpacity} group relative h-full`}>
      {item.is_featured && (
        <div className="absolute top-2 left-2 z-50 flex gap-1">
          <Badge className="text-xs py-1 px-2 rounded-md bg-yellow-500 text-yellow-900 hover:bg-yellow-500/80">
            <Star className="h-3 w-3 mr-1 fill-yellow-900" /> Featured
          </Badge>
        </div>
      )}
      <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
        <Badge className="text-xs py-1 px-2 rounded-md">Drag to reorder</Badge>
      </div>
      <div
        className="relative aspect-video w-full overflow-hidden bg-muted cursor-pointer"
        onClick={() => onEdit(item.id)}
      >
        <img
          src={displayImage}
          alt={itemTitle}
          className="h-full w-full object-cover"
          onError={(e) => {
            // Fallback to placeholder on error
            e.currentTarget.src = '/placeholder.svg';
          }}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
      </div>
      
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{item.title}</h3>
              {item.status === "draft" && (
                <Badge variant="secondary">Draft</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-3 overflow-hidden break-words">
              {stripHtmlTags(item.description)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(item.id)}>
                  <FileEdit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                {item.status === "published" && onUpdateStatus && (
                  <DropdownMenuItem onClick={() => onUpdateStatus(item.id, "draft")}>
                    <Undo className="mr-2 h-4 w-4" />
                    Revert to Draft
                  </DropdownMenuItem>
                )}
                {item.status === "draft" && onUpdateStatus && (
                  <DropdownMenuItem onClick={() => onUpdateStatus(item.id, "published")}>
                    <FileEdit className="mr-2 h-4 w-4" />
                    Publish
                  </DropdownMenuItem>
                )}
                {item.is_featured ? (
                  <DropdownMenuItem disabled>
                    <Star className="mr-2 h-4 w-4 fill-current" />
                    Featured
                  </DropdownMenuItem>
                ) : (
                  onSetFeatured && (
                    <DropdownMenuItem onClick={() => onSetFeatured(item.id)}>
                      <Star className="mr-2 h-4 w-4" />
                      Set as Featured
                    </DropdownMenuItem>
                  )
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>

      <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Project Title</Label>
              <Input
                id="title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Enter project title"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRenameDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleRename}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Portfolio Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this portfolio item? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                onRemove(index);
                setIsDeleteDialogOpen(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default PortfolioItemCard;
